import { useRef, useState } from 'react'

import { useClickOutside } from '@/hooks/useClickOutside'
import { exchangeColor, exchangeLabel } from '@/lib/format'
import { CheckIcon, ChevronDownIcon, LayersIcon } from '@/components/common/icons'

interface ExchangeMultiSelectProps {
  options: string[]
  selected: string[]
  onChange: (next: string[]) => void
}

export function ExchangeMultiSelect({ options, selected, onChange }: ExchangeMultiSelectProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useClickOutside(ref, () => setOpen(false), open)

  const selectedSet = new Set(selected)
  const allSelected = options.length > 0 && selected.length === options.length

  function toggle(ex: string) {
    if (selectedSet.has(ex)) onChange(selected.filter((e) => e !== ex))
    else onChange([...options.filter((e) => selectedSet.has(e) || e === ex)])
  }

  const label = allSelected
    ? 'Все биржи'
    : selected.length === 0
      ? 'Биржи не выбраны'
      : selected.length === 1
        ? exchangeLabel(selected[0] as string)
        : `Биржи: ${selected.length}`

  return (
    <div className="msel" ref={ref}>
      <button
        type="button"
        className={`msel__btn${open ? ' is-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Биржи: ${label}`}
      >
        <LayersIcon size={15} className="input__icon" />
        <span className="msel__btn-label">{label}</span>
        <ChevronDownIcon size={15} className="msel__caret" />
      </button>

      {open ? (
        <div className="msel__pop" role="listbox" aria-label="Биржи">
          <button
            type="button"
            className={`msel__opt${allSelected ? ' is-checked' : ''}`}
            onClick={() => onChange(allSelected ? [] : [...options])}
          >
            <span className="msel__check">
              <CheckIcon size={12} />
            </span>
            <span>Все биржи</span>
          </button>
          {options.map((ex) => {
            const checked = selectedSet.has(ex)
            return (
              <button
                key={ex}
                type="button"
                role="option"
                aria-selected={checked}
                className={`msel__opt${checked ? ' is-checked' : ''}`}
                onClick={() => toggle(ex)}
              >
                <span className="msel__check">
                  <CheckIcon size={12} />
                </span>
                <span>{exchangeLabel(ex)}</span>
                <span className="msel__opt-dot" style={{ background: exchangeColor(ex) }} />
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
